import { UserInputError } from "./errors-CEDWDXew.js";
import { ParamOrganizationSlug, ParamRegionUrl } from "./schema-DKEjeVqP.js";
import { defineTool } from "./define-SD6AQa4b.js";
import { apiServiceFromContext } from "./api-EaEPyKlQ.js";
import { structuredResult } from "./results-NF1RJbDZ.js";
import { formatActor } from "./api-formatting-nIGXUFdx.js";
import { filterDashboardsByProjectConstraint, resolveDashboardProjectConstraint } from "./dashboards-BCwvH2V2.js";
import { z } from "zod";
import { setTag } from "@sentry/core";

//#region src/tools/catalog/find-dashboards.ts
const PROJECT_DASHBOARD_CURSOR_PREFIX = "mcp-dashboard-project:";
const dashboardSummarySchema = z.object({
	id: z.string(),
	title: z.string(),
	widgetCount: z.number().int().nonnegative(),
	widgetTypes: z.array(z.string()),
	projects: z.array(z.string()),
	environments: z.array(z.string()),
	createdBy: z.string().nullable(),
	dateCreated: z.string(),
	lastVisited: z.string().nullable(),
	isFavorited: z.boolean(),
	prebuiltId: z.string().nullable(),
	url: z.string().url()
});
const findDashboardsOutputSchema = z.object({
	dashboards: z.array(dashboardSummarySchema),
	nextCursor: z.string().nullable()
});
function isProjectDashboardCursor(value) {
	if (!value || typeof value !== "object") return false;
	const cursor = value;
	return cursor.v === 1 && (typeof cursor.apiCursor === "string" || cursor.apiCursor === null) && typeof cursor.offset === "number" && Number.isInteger(cursor.offset) && cursor.offset >= 0 && typeof cursor.pageLimit === "number" && Number.isInteger(cursor.pageLimit) && cursor.pageLimit > 0 && cursor.pageLimit <= 100;
}
/**
* Encodes the project-scoped pagination state that Sentry's dashboard API
* cannot represent: a visible-item offset inside one upstream API page.
*/
function encodeProjectDashboardCursor(cursor) {
	const payload = Buffer.from(JSON.stringify({
		v: 1,
		...cursor
	})).toString("base64url");
	return `${PROJECT_DASHBOARD_CURSOR_PREFIX}${payload}`;
}
/** Decodes MCP-owned project cursors while leaving raw Sentry API cursors intact. */
function decodeProjectDashboardCursor(cursor) {
	if (!cursor?.startsWith(PROJECT_DASHBOARD_CURSOR_PREFIX)) return null;
	try {
		const value = JSON.parse(Buffer.from(cursor.slice(22), "base64url").toString("utf8"));
		if (isProjectDashboardCursor(value)) return value;
	} catch {}
	throw new UserInputError("Invalid dashboard cursor. Pass the cursor exactly as returned by find_dashboards.");
}
/** Ensures org-wide dashboard searches only pass raw Sentry cursors upstream. */
function assertRawDashboardCursor(cursor) {
	if (!cursor?.startsWith(PROJECT_DASHBOARD_CURSOR_PREFIX)) return;
	throw new UserInputError("Project-scoped dashboard cursors can only be used in project-scoped dashboard searches. Start a new org-wide find_dashboards request without a cursor.");
}
/** Formats the next project cursor without exposing raw API cursors for constrained pages. */
function formatProjectDashboardNextCursor({ apiCursor, offset = 0, pageLimit }) {
	if (!apiCursor) return null;
	return encodeProjectDashboardCursor({
		apiCursor,
		offset,
		pageLimit
	});
}
/**
* Lists dashboards visible to one project, refilling across API pages while
* keeping Sentry's cursor page size stable for every followed API cursor.
*/
async function listProjectVisibleDashboards({ apiService, organizationSlug, titleQuery, sort, limit, cursor, projectId }) {
	const projectCursor = decodeProjectDashboardCursor(cursor);
	const dashboards = [];
	const seenCursors = /* @__PURE__ */ new Set();
	const pageLimit = projectCursor?.pageLimit ?? limit;
	let pageCursor = projectCursor ? projectCursor.apiCursor : cursor ?? null;
	let visibleOffset = projectCursor?.offset ?? 0;
	let nextCursor = null;
	if (pageCursor) seenCursors.add(pageCursor);
	do {
		const page = await apiService.listDashboards({
			organizationSlug,
			query: titleQuery ?? void 0,
			sortBy: sort,
			limit: pageLimit,
			cursor: pageCursor ?? void 0
		});
		const visiblePage = filterDashboardsByProjectConstraint({
			dashboards: page.dashboards,
			projectId
		});
		const offsetVisiblePage = visiblePage.slice(visibleOffset);
		const remaining = limit - dashboards.length;
		if (offsetVisiblePage.length > remaining) {
			dashboards.push(...offsetVisiblePage.slice(0, remaining));
			return {
				dashboards,
				nextCursor: encodeProjectDashboardCursor({
					apiCursor: pageCursor,
					offset: visibleOffset + remaining,
					pageLimit
				})
			};
		}
		dashboards.push(...offsetVisiblePage);
		visibleOffset = 0;
		nextCursor = page.nextCursor;
		if (!nextCursor || seenCursors.has(nextCursor)) {
			nextCursor = null;
			break;
		}
		seenCursors.add(nextCursor);
		pageCursor = nextCursor;
	} while (dashboards.length < limit);
	return {
		dashboards,
		nextCursor: formatProjectDashboardNextCursor({
			apiCursor: nextCursor,
			pageLimit
		})
	};
}
var find_dashboards_default = defineTool({
	name: "find_dashboards",
	skills: ["inspect"],
	requiredScopes: ["org:read"],
	description: [
		"Find Sentry dashboards in an organization.",
		"",
		"Use this tool when you need to:",
		"- List dashboards in an organization",
		"- Find a dashboard ID before calling get_dashboard_details",
		"- Search dashboards by title",
		"",
		"<examples>",
		"find_dashboards(organizationSlug='my-organization')",
		"find_dashboards(organizationSlug='my-organization', titleQuery='errors')",
		"</examples>",
		"",
		"<hints>",
		"- Dashboard IDs are organization-scoped.",
		"- Use `get_dashboard_details` after finding the correct dashboard ID.",
		"</hints>"
	].join("\n"),
	inputSchema: {
		organizationSlug: ParamOrganizationSlug,
		regionUrl: ParamRegionUrl.nullable().default(null),
		titleQuery: z.string().trim().describe("Optional title substring to search for.").nullable().default(null),
		sort: z.enum([
			"title",
			"-title",
			"dateCreated",
			"-dateCreated"
		]).describe("Sort order for dashboard results.").default("title"),
		cursor: z.string().trim().describe("Optional pagination cursor from a previous response. Reuse cursors only with the same search scope and project constraint.").nullable().default(null),
		limit: z.number().int().positive().max(100).describe("Maximum number of dashboards to return.").default(10)
	},
	annotations: {
		readOnlyHint: true,
		destructiveHint: false,
		openWorldHint: true
	},
	outputSchema: findDashboardsOutputSchema,
	async handler(params, context) {
		const apiService = apiServiceFromContext(context, { regionUrl: params.regionUrl ?? void 0 });
		const organizationSlug = params.organizationSlug;
		setTag("organization.slug", organizationSlug);
		const scopedProject = await resolveDashboardProjectConstraint({
			apiService,
			organizationSlug,
			scopedProjectSlug: context.constraints.projectSlug
		});
		if (scopedProject) {
			setTag("project.slug", scopedProject.slug);
			setTag("project.id", String(scopedProject.id));
		}
		let dashboards;
		let nextCursor;
		if (scopedProject) ({dashboards, nextCursor} = await listProjectVisibleDashboards({
			apiService,
			organizationSlug,
			titleQuery: params.titleQuery,
			sort: params.sort,
			limit: params.limit,
			projectId: scopedProject.id,
			cursor: params.cursor
		}));
		else {
			assertRawDashboardCursor(params.cursor);
			({dashboards, nextCursor} = await apiService.listDashboards({
				organizationSlug,
				query: params.titleQuery ?? void 0,
				sortBy: params.sort,
				limit: params.limit,
				cursor: params.cursor ?? void 0
			}));
		}
		return structuredResult({
			dashboards: dashboards.map((dashboard) => ({
				id: String(dashboard.id),
				title: dashboard.title,
				widgetCount: dashboard.widgetPreview.length,
				widgetTypes: Array.from(new Set(dashboard.widgetDisplay)),
				projects: dashboard.projects.map(String),
				environments: dashboard.environment,
				createdBy: dashboard.createdBy ? formatActor(dashboard.createdBy) : null,
				dateCreated: dashboard.dateCreated,
				lastVisited: dashboard.lastVisited ?? null,
				isFavorited: dashboard.isFavorited ?? false,
				prebuiltId: dashboard.prebuiltId === null || dashboard.prebuiltId === void 0 ? null : String(dashboard.prebuiltId),
				url: apiService.getDashboardUrl(organizationSlug, String(dashboard.id), { projectId: scopedProject?.id ?? (dashboard.projects.length === 1 ? dashboard.projects[0] : null) })
			})),
			nextCursor
		});
	}
});

//#endregion
export { findDashboardsOutputSchema, find_dashboards_default };
//# sourceMappingURL=find-dashboards-DE0x5cIv.js.map